import {Component, OnInit, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {ReportsService} from '../../data/reports.service';
import {QbEstimateDTO} from '../../data/qb-estimate-dto';
import {ProjectActivityService} from '../../../project-misc/data/project/activity/project-activity.service';
import {MiscSnackBarService} from 'projects/core/src/lib/misc/view/snackbar/misc-snackbar.service';
import {trackEntityById} from 'projects/core/src/public-api';
import {ConfirmationService} from 'primeng/api';
import * as moment from 'moment';
import {ProjectService} from '../../../project-misc/data/project/project.service';
import {ShortProject} from '../../../project-misc/data/project/short-project';

@Component({
  selector: 'app-estimate-editor-dialog',
  templateUrl: './estimate-editor-dialog.component.html',
  styleUrls: ['./estimate-editor-dialog.component.scss']
})
export class EstimateEditorDialogComponent implements OnInit {
  estimate: QbEstimateDTO;
  isEditing: boolean;
  isSplitting: boolean;

  txnDate: Date;
  amount: number;
  amountDisplay: string;
  percentage: number;
  percentageDisplay: string;
  selectedProject: ShortProject;

  splitDate: Date;
  splitPercentage: number;
  splitPercentageDisplay: string;
  splitAmount: number;
  splitAmountDisplay: string;

  projects: ShortProject[] = [];
  projectSearchTerm = '';
  saving = false;

  projectEstimates: QbEstimateDTO[] = [];
  loadingEstimates = false;
  showEstimatesList = false;
  trackEntityById = trackEntityById;

  constructor(
    protected dialogRef: MatDialogRef<EstimateEditorDialogComponent>,
    protected reportsService: ReportsService,
    protected projectsService: ProjectService,
    protected projectActivityService: ProjectActivityService,
    protected snackBarService: MiscSnackBarService,
    protected confirmationService: ConfirmationService,
    @Inject(MAT_DIALOG_DATA) private data: {
      estimate?: QbEstimateDTO,
      startDate: string,
      projectId?: number,
      finishDate: string
    }) {
    if (data.estimate) {
      this.estimate = data.estimate;
      this.isEditing = true;
      this.txnDate = new Date(data.estimate.txnDate);
      this.amount = data.estimate.amount;
      this.amountDisplay = this.amount ? '$' + this.amount.toFixed(2) : '$0.00';
      this.percentage = data.estimate.percentage;
      this.percentageDisplay = this.percentage ? this.percentage.toFixed(2) + '%' : '0.00%';
      this.splitDate = new Date(this.txnDate.getFullYear(), this.txnDate.getMonth(), 0);
    } else {
      this.isEditing = false;
      this.txnDate = new Date();
      this.amountDisplay = '$0.00';
    }
  }

  ngOnInit(): void {
    this.projectsService.getShortLastProjects().subscribe(projects => {
      this.projects = projects
      if (this.estimate?.projectId) {
        this.selectedProject = this.projects.find(p => p.id === this.estimate.projectId);
      } else if (this.data.projectId) {
        this.selectedProject = this.projects.find(p => p.id === this.data.projectId);
      }
    });
    if (this.data.estimate.projectId) {
      this.loadProjectEstimates(this.estimate.projectId);
    } else if (this.data.projectId) {
      this.loadProjectEstimates(this.data.projectId);
    }
  }

  loadProjectEstimates(projectId: number, selectFirst = false) {
    if (!projectId) {
      return;
    }
    this.showEstimatesList = true;
    this.loadingEstimates = true;
    this.reportsService.getEstimatesByProjectId(projectId).subscribe(estimates => {
      this.projectEstimates = estimates.sort((a, b) => {
        const aTime = a.txnDate ? new Date(a.txnDate).getTime() : 0;
        const bTime = b.txnDate ? new Date(b.txnDate).getTime() : 0;
        return bTime - aTime;
      }).reverse();
      this.loadingEstimates = false;
      if (selectFirst && this.projectEstimates.length > 0) {
        this.editEstimate(this.projectEstimates[0]);
      }
      if (this.projectEstimates.length === 0) {
        this.dialogRef.close(true);
      }
    }, error => {
      this.loadingEstimates = false;
    });
  }

  onProjectChange() {
    if (this.selectedProject?.id) {
      this.isEditing = false;
      this.isSplitting = false;
      this.estimate = null;
      this.txnDate = new Date();
      this.amount = null;
      this.amountDisplay = '$0.00';
      this.percentage = null;
      this.percentageDisplay = '0.00%';
      this.loadProjectEstimates(this.selectedProject.id);
    } else {
      this.showEstimatesList = false;
      this.projectEstimates = [];
    }
  }


  save() {
    if (this.saving) {
      return;
    }

    this.saving = true;

    if (this.isEditing) {
      this.updateEstimate();
    } else {
      this.createEstimate();
    }
  }

  createEstimate() {
    if (!this.selectedProject || !this.amount) {
      this.saving = false;
      if (!this.selectedProject) {
        this.snackBarService.openWarningSnackBar('Please select a project');
      } else if (!this.amount || this.amount <= 0) {
        this.snackBarService.openWarningSnackBar('Please enter an amount');
      }
      return;
    }

    const txnDateStr = moment(this.txnDate).format('YYYY-MM-DD');
    this.reportsService.createEstimate(txnDateStr, this.selectedProject.id, this.amount).subscribe((res: QbEstimateDTO) => {
      this.saving = false;
      this.loadProjectEstimates(this.selectedProject.id);
      this.editEstimate(res);
    }, error => {
      this.saving = false;
      this.snackBarService.handleError(error, 'Error creating estimate');
    });
  }

  updateEstimate() {
    const updateData: Partial<QbEstimateDTO> = {};

    const estimateTxnDate = new Date(this.estimate.txnDate);
    if (this.txnDate.getTime() !== estimateTxnDate.getTime()) {
      updateData.txnDate = this.txnDate;
    }

    if (this.estimate.parentId) {
      if (this.percentage !== this.estimate.percentage) {
        updateData.percentage = this.percentage;
      }
    } else {
      if (this.amount !== this.estimate.amount) {
        updateData.amount = this.amount;
      }
    }

    if (Object.keys(updateData).length === 0) {
      this.dialogRef.close(false);
      return;
    }

    this.reportsService.updateEstimate(this.estimate.id, updateData).subscribe(() => {
      this.saving = false;
      this.loadProjectEstimates(this.estimate.projectId, true);
    }, error => {
      this.saving = false;
      this.snackBarService.handleError(error, 'Error updating estimate');
    });
  }

  split() {
    if (!this.splitDate || !this.splitPercentage || this.splitPercentage <= 0 || this.splitPercentage >= 100) {
      this.snackBarService.openErrorSnackBar('Please enter valid split date and percentage');
      return;
    }

    if (this.saving) {
      return;
    }
    this.saving = true;

    const splitDateStr = moment(this.splitDate).format('YYYY-MM-DD');
    this.reportsService.splitEstimate(this.estimate.id, this.splitPercentage, splitDateStr).subscribe((res: QbEstimateDTO) => {
      this.saving = false;
      this.isSplitting = false;
      if (this.estimate?.projectId) {
        this.loadProjectEstimates(this.estimate.projectId);
        this.editEstimate(res);
      }
    }, error => {
      this.saving = false;
      this.snackBarService.handleError(error, 'Error splitting estimate');
    });
  }

  toggleSplitMode() {
    this.isSplitting = !this.isSplitting;
  }

  private sanitizeNumericInput(value: string, maxDecimals = 2): string {
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > maxDecimals) {
      value = parts[0] + '.' + parts[1].substring(0, maxDecimals);
    }
    return value;
  }

  private handleCurrencyInput(event: any, prop: 'amount' | 'splitAmount') {
    const value = this.sanitizeNumericInput(event.target.value);
    const displayProp = prop + 'Display';
    this[displayProp] = value ? '$' + value : '$';
    event.target.value = this[displayProp];
    const numValue = parseFloat(value);
    this[prop] = isNaN(numValue) ? 0 : numValue;
  }

  private handlePercentageInput(event: any, prop: 'percentage' | 'splitPercentage', updateCallback?: () => void) {
    let value = this.sanitizeNumericInput(event.target.value);
    let numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 100) {
      value = '100';
    }
    const displayProp = prop + 'Display';
    this[displayProp] = value ? value : '';
    event.target.value = this[displayProp];
    numValue = parseFloat(value);
    this[prop] = isNaN(numValue) ? 0 : Math.min(100, Math.max(0, numValue));
    if (updateCallback) {
      updateCallback();
    }
  }

  onAmountInput(event: any) {
    this.handleCurrencyInput(event, 'amount');
    if (this.estimate?.originalAmount && this.estimate.originalAmount > 0) {
      this.percentage = (this.amount / this.estimate.originalAmount) * 100;
      this.percentage = Math.min(100, Math.max(0, this.percentage));
      this.percentageDisplay = this.percentage.toFixed(2);
    }
  }

  onAmountFocus() {
    if (this.amountDisplay === '$0.00') {
      this.amountDisplay = '$';
    }
  }

  onAmountBlur() {
    const value = this.amountDisplay.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.amount = numValue;
      this.amountDisplay = '$' + this.amount.toFixed(2);
    } else {
      this.amount = 0;
      this.amountDisplay = '$0.00';
    }
    if (this.estimate?.originalAmount && this.estimate.originalAmount > 0) {
      this.percentage = (this.amount / this.estimate.originalAmount) * 100;
      this.percentage = Math.min(100, Math.max(0, this.percentage));
      this.percentageDisplay = this.percentage.toFixed(2) + '%';
    }
  }

  onPercentageInput(event: any) {
    this.handlePercentageInput(event, 'percentage', () => {
      if (this.estimate?.originalAmount) {
        this.amount = (this.estimate.originalAmount * this.percentage) / 100;
        this.amountDisplay = '$' + this.amount.toFixed(2);
      }
    });
  }

  onPercentageFocus() {
    if (this.percentageDisplay) {
      this.percentageDisplay = this.percentageDisplay.replace('%', '');
    }
  }

  onPercentageBlur() {
    const value = this.percentageDisplay?.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.percentage = Math.min(100, Math.max(0, numValue));
      this.percentageDisplay = this.percentage.toFixed(2) + '%';
    } else {
      this.percentage = 0;
      this.percentageDisplay = '0.00%';
    }
    if (this.estimate?.originalAmount) {
      this.amount = (this.estimate.originalAmount * this.percentage) / 100;
      this.amountDisplay = '$' + this.amount.toFixed(2);
    }
  }

  onSplitPercentageInput(event: any) {
    this.handlePercentageInput(event, 'splitPercentage', () => {
      if (this.estimate?.amount) {
        this.splitAmount = (this.estimate.amount * this.splitPercentage) / 100;
        this.splitAmountDisplay = '$' + this.splitAmount.toFixed(2);
      }
    });
  }

  onSplitPercentageFocus() {
    if (this.splitPercentageDisplay) {
      this.splitPercentageDisplay = this.splitPercentageDisplay.replace('%', '');
    }
  }

  onSplitPercentageBlur() {
    const value = this.splitPercentageDisplay?.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.splitPercentage = Math.min(100, Math.max(0, numValue));
      this.splitPercentageDisplay = this.splitPercentage.toFixed(2) + '%';
    } else {
      this.splitPercentage = 0;
      this.splitPercentageDisplay = '0.00%';
    }
    if (this.estimate?.amount) {
      this.splitAmount = (this.estimate.amount * this.splitPercentage) / 100;
      this.splitAmountDisplay = '$' + this.splitAmount.toFixed(2);
    }
  }

  onSplitAmountInput(event: any) {
    this.handleCurrencyInput(event, 'splitAmount');
    if (this.splitAmount > (this.estimate?.amount || 0)) {
      this.splitAmount = this.estimate?.amount || 0;
      this.splitAmountDisplay = '$' + this.splitAmount.toFixed(2);
    }
    if (this.estimate?.amount && this.estimate.amount > 0) {
      this.splitPercentage = (this.splitAmount / this.estimate.amount) * 100;
      this.splitPercentage = Math.min(100, Math.max(0, this.splitPercentage));
      this.splitPercentageDisplay = this.splitPercentage.toFixed(2) + '%';
    }
  }

  onSplitAmountFocus() {
    if (this.splitAmountDisplay === '$0.00') {
      this.splitAmountDisplay = '$';
    }
  }

  onSplitAmountBlur() {
    const value = this.splitAmountDisplay?.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      this.splitAmount = numValue;
      this.splitAmountDisplay = '$' + this.splitAmount.toFixed(2);
    } else {
      this.splitAmount = 0;
      this.splitAmountDisplay = '$0.00';
    }

    if (this.estimate?.amount && this.estimate.amount > 0) {
      this.splitPercentage = (this.splitAmount / this.estimate.amount) * 100;
      this.splitPercentage = Math.min(100, Math.max(0, this.splitPercentage));
      this.splitPercentageDisplay = this.splitPercentage.toFixed(2) + '%';
    }
  }

  cancel() {
    if (this.isSplitting) {
      this.isSplitting = false;
    } else {
      this.close();
    }
  }

  close() {
    this.dialogRef.close(true);
  }

  openInQb(estimate: QbEstimateDTO) {
    const environment = (window as any).environment || { dev: false };
    let baseLink = `https://app.qbo.intuit.com/app`;
    if (environment.dev) {
      baseLink = `https://app.sandbox.qbo.intuit.com/app`;
    }
    window.open(`${baseLink}/estimate?txnId=${estimate.qbTxnId}`, '_blank');
  }

  editEstimate(estimate: QbEstimateDTO) {
    this.estimate = estimate;
    this.txnDate = new Date(estimate.txnDate);
    this.amount = estimate.amount;
    this.amountDisplay = this.amount ? '$' + this.amount.toFixed(2) : '$0.00';
    this.percentage = estimate.percentage;
    this.percentageDisplay = this.percentage ? this.percentage.toFixed(2) + '%' : '0.00%';
    this.isEditing = true;
    this.isSplitting = false;
  }

  deleteEstimate() {
    if (!this.estimate || !this.estimate.createdManually) {
      return;
    }
    this.confirmationService.confirm({
      header: 'Delete Estimate?',
      message: `Delete estimate for ${this.estimate.projectName}?`,
      dismissableMask: true,
      icon: 'pi pi-trash',
      accept: () => {
        this.reportsService.deleteEstimate(this.estimate.id).subscribe(() => {
          this.loadProjectEstimates(this.estimate.projectId, true);
        });
      }
    });
  }
}
